import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { isEqual } from "lodash";
import { Flex, Text } from "styles/utility-components";

import { InlineSaveListButton } from "components/entities/save_components/lists/InlineSaveListButton";
import { useSaveListDrawer } from "components/entities/save_components/lists/SaveListDrawerProvider";
import { convertNoticeFiltersToSearchRequest, NoticeFilters } from "components/notices/utils";
import { Table } from "lib/core_components/Table";
import { useNoticesAnalysisByGroup } from "lib/hooks/api/notices/analysis/useNoticesAnalysisByGroup";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import { routes } from "lib/routes";
import { EventNames, useTracking } from "lib/tracking";
import {
  NoticeAnalysisByGroupGroupBy,
  NoticeAnalysisByGroupSort,
  NoticeAnalysisByGroupSortOrder,
} from "lib/types/graphQLEnums";
import { SavedViewType } from "lib/types/savedViews";
import { toLowercase } from "lib/utils/stringUtils";
import UIcon from "../../lib/icons/untitled_ui/UIcon";
import { isDefined } from "../../lib/utils";
import EmptyAnalysis from "./EmptyAnalysis";
import { addSelectedRowsToFilters } from "./ExportButton";
import HeadlineFigure from "./HeadlineFigure";
import NoticeAnalysisActionBar from "./NoticeAnalysisActionBar";
import NoticesModal from "./NoticesModal";
import ValueBar from "./ValueBar";

import css from "./TableStyles.module.scss";

const DEFAULT_SORT = {
  sort: NoticeAnalysisByGroupSort.NoticeCount,
  sortOrder: NoticeAnalysisByGroupSortOrder.Desc,
};

type ByGroupSort = {
  sort: NoticeAnalysisByGroupSort;
  sortOrder: NoticeAnalysisByGroupSortOrder;
};

type ByGroupProps = {
  filters: NoticeFilters;
  groupBy?: NoticeAnalysisByGroupGroupBy;
  setFilters: (newValue: React.SetStateAction<NoticeFilters | undefined>) => void;
  selectedView?: SavedViewType;
  selectedRows: string[];
  setSelectedRows: (selectedRows: string[]) => void;
};

function ByGroup({
  groupBy = NoticeAnalysisByGroupGroupBy.Buyer,
  filters,
  setFilters,
  selectedView,
  selectedRows,
  setSelectedRows,
}: ByGroupProps) {
  const { logEvent } = useTracking();

  const [sort, setSort] = useState<ByGroupSort>(DEFAULT_SORT);
  const [pageSize, setPageSize] = useTablePageSize("notice-analysis-by-group-table", 20);
  const [currentPage, setCurrentPage] = useState(1);
  const { openListDrawer } = useSaveListDrawer();
  const [modalFocus, setModalFocus] = useState<[string, string] | null>(null); // [guid, name]

  const { data, isLoading, isError } = useNoticesAnalysisByGroup(
    convertNoticeFiltersToSearchRequest(filters),
    {
      ...sort,
      groupBy,
      limit: pageSize,
      page: currentPage,
    },
  );
  const lowerCaseGroupBy = toLowercase(groupBy);

  // If group changes: reset the current page to 1 but keep pageSize the same
  useEffect(() => {
    setCurrentPage(1);
  }, [groupBy]);

  if (data?.analysis?.byGroup?.results.length === 0) {
    return (
      <EmptyAnalysis filters={filters} setFilters={setFilters} selectedViewId={selectedView?.id} />
    );
  }

  const handleSave = () => {
    openListDrawer({
      entityType: lowerCaseGroupBy,
      entityIds: selectedRows,
    });
  };

  const handleClear = () => {
    setSelectedRows([]);
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      setModalFocus(null);
    } else {
      console.warn("onOpenChange with open=true is unsupported in ByGroup");
    }
  };

  return (
    <Wrapper column flexGrow={1} gap={24}>
      <PrintOnlyText h1>
        Top {groupBy.toLowerCase()}s by {sort.sort.toLowerCase().replace("_", " ")}{" "}
        {selectedView?.name ? `- ${selectedView.name}` : ``}
      </PrintOnlyText>
      <Flex gap={24}>
        <HeadlineFigure
          label={enumToLabel(groupBy)}
          value={data?.analysis?.byGroup?.totalResults}
          valueType="number"
          isLoading={isLoading}
        />
        <HeadlineFigure
          label="Notice"
          value={data?.totalResults}
          valueType="number"
          isLoading={isLoading}
        />
        <HeadlineFigure
          label="Total Value"
          value={data?.analysis?.overall?.noticeValues}
          valueType="currency"
          isLoading={isLoading}
          noPluralise
        />
      </Flex>
      {selectedRows.length > 0 && (
        <NoticeAnalysisActionBar
          selectedRows={selectedRows}
          onSave={handleSave}
          onClear={handleClear}
        />
      )}
      <NoticesModal
        open={modalFocus !== null}
        onOpenChange={onOpenChange}
        showing={modalFocus?.[1] ?? ""}
        filters={addSelectedRowsToFilters({
          filters,
          selectedRows: [modalFocus?.[0]].filter(isDefined), // Know it will just be 1 row but that's expected
          groupBy,
        })}
        defaultColumnSettings={[
          "relevance_score",
          "title",
          groupBy === NoticeAnalysisByGroupGroupBy.Buyer ? "suppliers" : "buyer",
          "value",
          "published_date",
        ]}
      />
      <Table
        dataSource={data?.analysis?.byGroup?.results}
        rowKey="guid"
        isError={isError}
        loading={isLoading}
        tableLayout="fixed"
        columns={[
          {
            key: "name",
            title: enumToLabel(groupBy),
            render: (_: unknown, record) => (
              <NameCell>
                <a href={enumToUrl(groupBy, record.guid)}>{record.name}</a>
                {record.isSavedCount > 0 && (
                  <BookmarkIcon icon="bookmark" color="sysPrimaryDefault" size={12} />
                )}
                <InlineSaveListButton
                  displayType="button"
                  entityType={lowerCaseGroupBy}
                  entityIds={[record.guid]}
                  savedCount={record.isSavedCount}
                />
              </NameCell>
            ),
          },
          {
            key: NoticeAnalysisByGroupSort.NoticeCount,
            title: "Notice count",
            sorter: true,
            sortOrder: getSortOrder(sort, NoticeAnalysisByGroupSort.NoticeCount),
            sortDirections: ["descend", "ascend", "descend"],
            render: (_: unknown, record) => (
              <ValueBar
                value={record.noticeCount}
                maxValue={data?.analysis?.byGroup?.maxNoticeCount}
                type="count"
                onClick={() => {
                  setModalFocus([record.guid, record.name]);
                }}
              />
            ),
          },
          {
            key: NoticeAnalysisByGroupSort.NoticeValues,
            title: "Notice value",
            sorter: true,
            sortOrder: getSortOrder(sort, NoticeAnalysisByGroupSort.NoticeValues),
            sortDirections: ["descend", "ascend", "descend"],
            render: (_: unknown, record) => (
              <ValueBar
                value={record.noticeValues}
                maxValue={data?.analysis?.byGroup?.maxNoticeValues}
                type="currency"
                onClick={() => {
                  setModalFocus([record.guid, record.name]);
                }}
              />
            ),
          },
        ]}
        rowSelection={{
          onChange: (selectedRowKeys) => {
            setSelectedRows(selectedRowKeys.map((key) => key.toString()));
          },
          preserveSelectedRowKeys: true,
          selectedRowKeys: selectedRows,
        }}
        pagination={{
          current: currentPage,
          pageSize,
          total: data?.analysis?.byGroup?.totalResults,
          onChange: (page, pageSize) => {
            setPageSize(pageSize);
            setCurrentPage(page);
            logEvent(EventNames.paginationSelected, { Value: page });
          },
          showSizeChanger: true,
          rootClassName: css.hideOnPrint,
        }}
        onChange={(_pagination, _filters, sorter) => {
          if (Array.isArray(sorter)) {
            sorter = sorter[0];
          }
          // If the sort order is not defined, we don't want to trigger a sort without an order
          if (!sorter.order) {
            return;
          }
          if (
            !Object.values(NoticeAnalysisByGroupSort).includes(
              sorter.columnKey as NoticeAnalysisByGroupSort,
            )
          ) {
            console.warn(
              `Unexpected sorter columnKey: ${sorter.columnKey}. Expected one of: ${Object.values(
                NoticeAnalysisByGroupSort,
              ).join(", ")}`,
            );
            return;
          }
          const newSortState: ByGroupSort = {
            sort: sorter.columnKey as NoticeAnalysisByGroupSort,
            sortOrder:
              sorter.order === "descend"
                ? NoticeAnalysisByGroupSortOrder.Desc
                : NoticeAnalysisByGroupSortOrder.Asc,
          };
          if (!isEqual(newSortState, sort)) {
            setSort(newSortState);
          }
        }}
      />
      <Flex justifyContent="space-between" alignItems="center" gap={8}>
        {(data?.analysis?.byGroup?.totalResults ?? 0) - pageSize > 0 && (
          <PrintOnlyText bold>
            + {(data?.analysis?.byGroup?.totalResults ?? 0) - pageSize} more {groupBy.toLowerCase()}
            s
          </PrintOnlyText>
        )}
        {currentPage !== 1 && (
          <PrintOnlyText color="sysTextPlaceholder">
            Showing results for page {currentPage}
            {data?.analysis?.byGroup?.totalResults !== undefined
              ? ` of ${Math.ceil(data?.analysis?.byGroup?.totalResults / pageSize)}`
              : ""}
          </PrintOnlyText>
        )}
      </Flex>
    </Wrapper>
  );
}

const enumToLabel = (enumValue: NoticeAnalysisByGroupGroupBy) => {
  switch (enumValue) {
    case NoticeAnalysisByGroupGroupBy.Buyer:
      return "Buyer";
    case NoticeAnalysisByGroupGroupBy.Supplier:
      return "Supplier";
  }
};

const enumToUrl = (enumValue: NoticeAnalysisByGroupGroupBy, guid: string) => {
  switch (enumValue) {
    case NoticeAnalysisByGroupGroupBy.Buyer:
      return `${routes.buyer}/${guid}`;
    case NoticeAnalysisByGroupGroupBy.Supplier:
      return `${routes.supplier}/${guid}`;
  }
};

const getSortOrder = (
  sort: ByGroupSort,
  key: NoticeAnalysisByGroupSort,
): "ascend" | "descend" | undefined => {
  if (sort.sort === key) {
    return sort.sortOrder === NoticeAnalysisByGroupSortOrder.Asc ? "ascend" : "descend";
  }
  return undefined;
};

const PrintOnlyText = styled(Text)({
  "@media screen": {
    display: "none",
  },
});

const Wrapper = styled(Flex)({
  maxWidth: "100%",
});

const NameCell = styled.div({
  display: "flex",
  alignItems: "center",
  gap: 8,
  justifyContent: "space-between",
  width: "100%",

  a: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1,
  },

  button: {
    visibility: "hidden",
    opacity: 0,
    flexShrink: 0,
  },

  "tr:hover &": {
    button: {
      visibility: "visible",
      opacity: 1,
    },
  },
});

const BookmarkIcon = styled(UIcon)({
  display: "flex",
  alignItems: "center",
  marginLeft: "8px",

  "tr:hover &": {
    visibility: "hidden",
    opacity: 0,
  },
});

export default ByGroup;
